import { useQuery } from "@tanstack/react-query";

import { api, requete } from "../../shared/api/client";
import { collecterToutesLesPages } from "../../shared/composants/exportCsv";
import type { Mouvement, Position } from "../../shared/api/types";

export function useMouvementsDuGrv(numero: string) {
  return useQuery({
    queryKey: ["mouvements", "grv", numero],
    queryFn: () =>
      api.lire<Mouvement[]>(`/v1/grv/${encodeURIComponent(numero)}/mouvements?pageSize=50`),
  });
}

export function useMouvementsDeZone(zone: string | undefined) {
  return useQuery({
    queryKey: ["mouvements", "zone", zone],
    queryFn: () => api.lire<Mouvement[]>("/v1/mouvements" + requete({ "filter[zone]": zone, pageSize: 50 })),
    enabled: Boolean(zone),
  });
}

// Plafond réel côté serveur (`common/http.py::TAILLE_PAGE_MAXIMALE`) : au-delà, `pageSize` est
// silencieusement ramené à cette valeur — demander plus n'aurait aucun effet, seul le curseur
// permet de tout récupérer (`collecterToutesLesPages`).
const TAILLE_PAGE_EXPORT = 200;

/**
 * Mouvements de plusieurs GRV sur une période, pour l'export « Séjours par géozone » de
 * `EcranCarte.tsx` — une requête (paginée par curseur) par GRV, en parallèle, puisqu'il n'existe
 * pas de route agrégée multi-GRV avec filtre de dates (seulement par GRV ou par zone,
 * `mouvements/handler.py`).
 */
export function useMouvementsDePlusieursGrv(
  numeros: string[],
  { debut, fin }: { debut: string; fin: string },
) {
  return useQuery({
    queryKey: ["mouvements", "export-sejours", numeros, debut, fin],
    queryFn: async () => {
      const parGrv = await Promise.all(
        numeros.map((numero) =>
          collecterToutesLesPages<Mouvement>(`/v1/grv/${encodeURIComponent(numero)}/mouvements`, {
            "filter[debut]": debut,
            "filter[fin]": fin,
            pageSize: TAILLE_PAGE_EXPORT,
          }),
        ),
      );
      return parGrv.flat();
    },
    enabled: numeros.length > 0,
  });
}

/**
 * Positions de plusieurs GRV sur une période, pour l'export « Détail » (toutes les positions,
 * avec position GPS) de `EcranCarte.tsx` — même principe que `useMouvementsDePlusieursGrv`.
 */
export function usePositionsDePlusieursGrv(
  numeros: string[],
  { debut, fin }: { debut: string; fin: string },
) {
  return useQuery({
    queryKey: ["positions", "export-detail", numeros, debut, fin],
    queryFn: async () => {
      const parGrv = await Promise.all(
        numeros.map((numero) =>
          collecterToutesLesPages<Position>(`/v1/grv/${encodeURIComponent(numero)}/positions`, {
            "filter[debut]": debut,
            "filter[fin]": fin,
            pageSize: TAILLE_PAGE_EXPORT,
          }),
        ),
      );
      return parGrv.flat();
    },
    enabled: numeros.length > 0,
  });
}
